import React from 'react';
import { SubComponentProps } from '../AccountRoot';
import OrderForm from './OrderForm';
import OrderReview from './OrderReview';
import toMoney from '../util/tomoney';
import { AcquireData, AcquireDataHome, AcquireDataOffice, OrderData, OrderDataItemDataNamePlate, OrderDataItemDataUrn, OrderDataItemPriceData, ReleaseData, ReleaseDataHome, ReleaseDataOffice } from '../classes/order';
import { AcquireDataType, ReleaseDataType, OrderPaymentType, OrderPaymentTypeDetail, HomeAddressInterface } from '../types/order';


export interface ConfTierPrice {
  max?: number;
  price: number;
}

export type PriceType = "fixed" | "byweight" | "tiered" | "fixed_additional" | "service_tiered";

export type UrnDetailType = "" | "cat" | "dog" | "paw";

export interface ConfPricing {
  pricetype: PriceType;
  label?: string;
  price?: number;
  price_additional?: number;
  prices?: ConfTierPrice[];
  sprices?: {
    [service: string]: number
  };
}
export interface ConfOption {
  tooltip?: string;
  label: string;
  tag: string;
  pricing: ConfPricing;
  engraving_price?: number;
}
export interface ConfType extends ConfOption {
  description?: string
}


export interface OrderFormProps extends SubComponentProps {
  max_weight: number;
  types: ConfType[];
  urns: ConfOption[];
  nameplate: ConfOption;
  pawprint: ConfOption;
  addon_viewing: ConfOption;
  addon_priority: ConfOption;
  addon_viewing_and_priority: ConfOption;
  addon_after_hours_appt: ConfOption;
  acquire_fees: TransportFee[];
  release_fees: TransportFee[];
  FC: any;
}


export interface TransportFee {
  label: string
  tag: string
  type: string
  pricing: ConfPricing;
}


export interface OrderFormStateUnitPrices {
  private: number;
  communal: number;
  burial: number;
}


export interface OrderFormStateNameplate {
  line1: string;
  line2: string;
  line3: string;
}


export interface AquireConf {
  type: "standard" | "home" | "dropoff";
  detail: undefined | HomeDeliveryConf | OfficeDropOffConf;
}


export interface ReleaseConf {
  type: "standard" | "home" | "pickup";
  detail: undefined | HomeDeliveryConf | OfficeDropOffConf;
}


export interface HomeDeliveryConf {
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  requested_time: string | undefined;
}


export interface OfficeDropOffConf {
  requested_time: string | undefined;
}


export interface OrderFormState {
  loading: boolean;
  reviewing: boolean;

  pet_name: string;
  pet_species: string;
  weight: number | "";
  owner_name: string;
  owner_phone: string;
  owner_email: string;

  addr_address1: string;
  addr_address2: string;
  addr_city: string;
  addr_state: string;
  addr_zip: string;

  service: ConfType | undefined;

  extras_urn: ConfType | undefined;
  extras_urn_detail?: UrnDetailType;

  extras_nameplate: ConfType | undefined;
  nameplate_line1: string;
  nameplate_line2: string;
  nameplate_line3: string;
  
  extras_pawprint: ConfType | undefined;
  extras_pawprint_qty: number;

  extras_addon_viewing: ConfType | undefined;
  extras_addon_priority: ConfType | undefined;
  extras_addon_viewing_and_priority: ConfType | undefined;
  extras_addon_after_hours_appt: ConfType | undefined;

  comments: string;

  payment_type: OrderPaymentType;
  payment_type_detail: OrderPaymentTypeDetail | undefined;
  payment_comment: string;
  payment_cash_amount?: string;
  payment_check_number?: string;

  acquire_type: AcquireDataType;
  acquire_use_main_address: boolean;
  acquire_home_addr_name: string;
  acquire_home_addr_address1: string;
  acquire_home_addr_address2: string;
  acquire_home_addr_city: string;
  acquire_home_addr_state: string;
  acquire_home_addr_zip: string;
  acquire_home_addr_phone: string;
  acquire_time: "" | Date;

  release_type: ReleaseDataType;
  release_use_main_address: boolean;
  release_home_addr_name: string;
  release_home_addr_address1: string;
  release_home_addr_address2: string;
  release_home_addr_city: string;
  release_home_addr_state: string;
  release_home_addr_zip: string;
  release_home_addr_phone: string;
  release_time: "" | Date;
  release_require_signature?: boolean;

}



class OrderFormRoot extends React.PureComponent<OrderFormProps, OrderFormState> {

  
  state: OrderFormState;

  baseState: OrderFormState = {
    loading: false,
    reviewing: false,

    pet_name: '',
    pet_species: '',
    weight: '',
    owner_name: '',
    owner_phone: '',
    owner_email: '',

    addr_address1: '',
    addr_address2: '',
    addr_city: '',
    addr_state: '',
    addr_zip: '',

    comments: '',

    service: undefined,
    extras_urn: undefined,
    extras_urn_detail: '' as UrnDetailType,

    extras_nameplate: undefined,
    nameplate_line1: '',
    nameplate_line2: '',
    nameplate_line3: '',

    extras_pawprint: undefined,
    extras_pawprint_qty: 1,

    extras_addon_viewing: undefined,
    extras_addon_priority: undefined,
    extras_addon_viewing_and_priority: undefined,
    extras_addon_after_hours_appt: undefined,

    payment_type: 'contact',
    payment_type_detail: undefined,
    payment_cash_amount: "",
    payment_check_number: "",    
    payment_comment: "",

    acquire_type: "home",
    acquire_home_addr_name: "",
    acquire_home_addr_address1: "",
    acquire_home_addr_address2: "",
    acquire_home_addr_city: "",
    acquire_home_addr_state: "",
    acquire_home_addr_zip: "",
    acquire_home_addr_phone: "",
    acquire_time: "",
    acquire_use_main_address: true,

    release_type: "home",
    release_home_addr_name: "",
    release_home_addr_address1: "",
    release_home_addr_address2: "",
    release_home_addr_city: "",
    release_home_addr_state: "",
    release_home_addr_zip: "",
    release_home_addr_phone: "",
    release_time: "",
    release_use_main_address: true,
  }


  constructor(props: OrderFormProps) {
    super(props);

    this.state = {
      ...this.baseState,
      loading: props.loading,
      payment_type: this.props.user?.data?.allow_billing_later ? 'invoice' : 'contact',
    };
  }


  componentDidUpdate(prevProps: OrderFormProps, prevState: OrderFormState) {
    if (this.state.reviewing !== prevState.reviewing) {
      this.scrollToTop();
    }
  }


  render() {

    return (
    <React.Fragment>
      <div id="OrderFormRootTop"></div>

      <div className="container">
        <div className="pull-right hidden" data-fc-id="minicart">
          <a href="https://preciouspetsms.foxycart.com/cart?cart=view"><i className="fa fa-shopping-cart"></i></a>
        </div>
      </div>

      <div className={'OrderRootContainer' + (this.state.reviewing ? ' reviewing' : '')}>
        <div className="PubOrder1">
          <OrderForm 
            {...this.props} 
            {...this.state}
            update={this.update}
            updateField={this.updateField} 
            updateFieldWeight={this.updateFieldWeight} 
            />
        </div>
        <div className="PubOrder2">

          <OrderReview 
            {...this.props} 
            {...this.state}
            createOrderRecord={this.createOrderRecord}
            update={this.update} 
            resetState={this.resetState} />

        </div>
      </div>     

    </React.Fragment>
    );

    /*

            update={this.update}
            setAcquireType={this.setAcquireType}
            setReleaseType={this.setReleaseType}
            updateAcquireField={this.updateAcquireField}
            updateReleaseField={this.updateReleaseField}
            createOrderRecord={this.createOrderRecord} 

          <OrderReview 
            {...this.props} 
            {...this.state}
            createOrderRecord={this.createOrderRecord}
            update={this.update} 
            resetState={this.resetState} />
    */
  }


  scrollToTop() {
    const el = document.getElementById('OrderFormRootTop');
    const b = el!.getBoundingClientRect();
    if (!el) return;
    window.scroll({
      top: b.top,
      left: b.left,
      behavior: 'smooth',
    })
  }


  update = (field: string, val: any) => {
    let update = {};
    (update as any)[field] = val;

    if (field === 'extras_addon_viewing_and_priority' && val) {
      // remove individual products if a bundle product is purchased
      (update as any)['extras_addon_priority'] = undefined;
      (update as any)['extras_addon_viewing'] = undefined;

    } else if (['extras_addon_priority', 'extras_addon_viewing'].includes(field) && val) {

      // add ONLY the bundle if all individual bundle products are purchased
      if (val && (
          field === "extras_addon_viewing" && this.state.extras_addon_priority 
          || field === "extras_addon_priority" && this.state.extras_addon_viewing
        )
      ) {
        update = {};
        (update as any)['extras_addon_viewing_and_priority'] = this.props.addon_viewing_and_priority;
        (update as any)['extras_addon_priority'] = undefined;
        (update as any)['extras_addon_viewing'] = undefined;

        } else {
        // remove bundle product if a single individual product is purchased
        (update as any)['extras_addon_viewing_and_priority'] = undefined;
      }

    }


    this.setState(update as OrderFormState);
    return true
  }


  updateField = (e:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    let k = e.target.name;
    let v: string | number = e.target.value;
    if (e.target.type === 'number') {
      v = parseInt(v);
    }
    const update: any = {};
    if (k === 'release_require_signature') {
      update[k] = v == 1;
    } else {
      update[k] = v;
    }

    this.setState(update as OrderFormState);
    return true;
  }


  updateFieldWeight = (e: React.ChangeEvent<HTMLInputElement>) => {
    let update = {};
    let k = e.target.name;
    let val = parseInt(e.target.value);
    (update as any)[k] = val > this.props.max_weight ? this.props.max_weight : val || ''; 
    this.setState(update as OrderFormState);
    return true;
  }


  setAcquireType = (type: AcquireDataType) => {
    /*
    const od = this.state.order_data;
    const aq = od.acquire;
    
    if (type === "home") {
      const d: HomeAddressInterface | OfficeVisitInterface = aq.detail ? { ...aq.detail } : {
        name: od.info.owner_name,
        address1: "",
        address2: "",
        city: "",
        state: 'CA',
        zip: "",
        phone: od.info.owner_phone,
        requested_time: undefined,  
      };

    
      if ('name' in d && !d.name) {
        d.name = od.info.owner_name;
      }
  
      if ('phone' in d && !d.phone) {
        d.phone = od.info.owner_phone;
      }

      const update: Partial<OrderData> = {
        acquire: new AcquireData(type, d)
      };

      this.setState(update as OrderFormState);
  
    } else if (type === "dropoff") {
      const d: HomeAddressInterface | OfficeVisitInterface = aq.detail ? { ...aq.detail } : {
        requested_time: undefined,  
      };

      const update = {
        acquire: {
          type: type as AcquireDataType,
          detail: d,
        } as AcquireData,
      };

      this.setState(update as OrderFormState);

    } else if (type === "standard") {

      const update = {
        acquire: {
          type: type as AcquireDataType,
          detail: { ...aq.detail },
        } as AcquireData,
      };

      this.setState(update as OrderFormState);
    }
    */
  }


  setReleaseType = (type: ReleaseDataType) => {
    /*
    const aq = this.state.release;
    
    if (type === "home") {
      const d: HomeAddressConf | OfficeVisitConf = aq.detail ? { ...aq.detail } : {
        name: this.state.owner_name,
        address1: "",
        address2: "",
        city: "",
        state: 'CA',
        zip: "",
        phone: this.state.owner_phone,
        requested_time: undefined,  
      };

    
      if ('name' in d && !d.name) {
        d.name = this.state.owner_name;
      }
  
      if ('phone' in d && !d.phone) {
        d.phone = this.state.owner_phone;
      }

      const update = {
        release: {
          type: type as ReleaseDataType,
          detail: d,
        } as ReleaseData,
      };

      this.setState(update as OrderFormState);
  
    } else if (type === "pickup") {
      const d: HomeAddressConf | OfficeVisitConf = aq.detail ? { ...aq.detail } : {
        requested_time: undefined,  
      };

      const update = {
        release: {
          type: type as ReleaseDataType,
          detail: d,
        } as ReleaseData,
      };

      this.setState(update as OrderFormState);

    } else if (type === "standard") {

      const update = {
        release: {
          type: type as ReleaseDataType,
          detail: { ...aq.detail },
        } as ReleaseData,
      };

      this.setState(update as OrderFormState);
    }
    */
  }


  updateAcquireField = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | Date | null) => {
    /*
    if (e === null || 'toJSON' in e) {
      const dd = (this.state.acquire.detail ? {...this.state.acquire.detail} : {} as HomeAddressConf);
      dd.requested_time = e === null ? undefined : e.toJSON();
      const acquire = { ...this.state.acquire, detail: dd };
      this.setState({ acquire } as OrderFormState);
    } else {
      let name = e.target.name.substr('acquire.'.length);
      let value = e.target.value;
      const dd = (this.state.acquire.detail ? {...this.state.acquire.detail} : {} as HomeAddressConf);
      (dd as any)[name] = value;
      const acquire = { ...this.state.acquire, detail: dd };
      this.setState({ acquire } as OrderFormState);
    }

    return true;
    */
  }


  updateReleaseField = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | Date | null) => {
    /*
    if (e === null || 'toJSON' in e) {
      const dd = (this.state.release.detail ? {...this.state.release.detail} : {} as HomeAddressConf);
      dd.requested_time = e === null ? undefined : e.toJSON();
      const release = { ...this.state.release, detail: dd };
      this.setState({ release } as OrderFormState);
    } else {
      let name = e.target.name.substr('release.'.length);
      let value = e.target.value;
      const dd = (this.state.release.detail ? {...this.state.release.detail} : {} as HomeAddressConf);
      (dd as any)[name] = value;
      const release = { ...this.state.release, detail: dd };
      this.setState({ release } as OrderFormState);
    }

    return true;
    */
  }



  updateHomeDeliveryTime = (d: Date) => {
  }


  updateOrderData = (field: string, val: any) => {
    let update = {};
    (update as any)[field] = val;
    this.setState(update as OrderFormState);
    return true
  }


  resetState = () => {
    this.setState({
      ...this.baseState,
      payment_type: this.props.user?.data?.allow_billing_later ? 'invoice' : 'contact',
    });
  }


  createOrderRecord = (): OrderData => {

    const odata = new OrderData();
    const s = this.state;

    const info = odata.info;
    info.pet_name = s.pet_name;
    info.pet_species = s.pet_species;
    info.weight = s.weight || 0;
    info.owner_name = s.owner_name;
    info.owner_phone = s.owner_phone;
    info.owner_email = s.owner_email;

    info.addr_line1 = s.addr_address1;
    info.addr_line2 = s.addr_address2;
    info.addr_city = s.addr_city;
    info.addr_state = s.addr_state;
    info.addr_zip = s.addr_zip;

    odata.acquire = new AcquireData([]);
    if (s.acquire_type === "home") {

      if (this.state.acquire_use_main_address) {

        odata.acquire = new AcquireDataHome({
          name: s.owner_name,
          address1: s.addr_address1,
          address2: s.addr_address2,
          city: s.addr_city,
          state: s.addr_state,
          zip: s.addr_zip,
          phone: s.owner_phone,
          requested_time: s.acquire_time || undefined,
        }, this.props.acquire_fees);

      } else {

        odata.acquire = new AcquireDataHome({
          name: s.acquire_home_addr_name,
          address1: s.acquire_home_addr_address1,
          address2: s.acquire_home_addr_address2,
          city: s.acquire_home_addr_city,
          state: s.acquire_home_addr_state,
          zip: s.acquire_home_addr_zip,
          phone: s.acquire_home_addr_phone,
          requested_time: s.acquire_time || undefined,
        }, this.props.acquire_fees);
      }      

    } else if (s.acquire_type === "dropoff") {

      odata.acquire = new AcquireDataOffice({
        requested_time: s.acquire_time || undefined,
      }, this.props.acquire_fees);
 
    } 


    odata.release = new ReleaseData([]);
    if (s.release_type === "home") {
      
      if (this.state.release_use_main_address) {

        odata.release = new ReleaseDataHome({
          name: s.owner_name,
          address1: s.addr_address1,
          address2: s.addr_address2,
          city: s.addr_city,
          state: s.addr_state,
          zip: s.addr_zip,
          phone: s.owner_phone,
          requested_time: s.release_time || undefined,
          require_signature: !!s.release_require_signature,
        }, this.props.release_fees);

      } else {

        odata.release = new ReleaseDataHome({
          name: s.release_home_addr_name,
          address1: s.release_home_addr_address1,
          address2: s.release_home_addr_address2,
          city: s.release_home_addr_city,
          state: s.release_home_addr_state,
          zip: s.release_home_addr_zip,
          phone: s.release_home_addr_phone,
          requested_time: s.release_time || undefined,
          require_signature: !!s.release_require_signature,
        }, this.props.release_fees);
      }

    } else if (s.release_type === "pickup") {

      odata.release = new ReleaseDataOffice({
        requested_time: s.release_time || undefined,
      }, this.props.release_fees);
 
    }


    // convert items
    if (s.service) {
      const ss = s.service;
      odata.addItem('service', ss.label, ss.tag, 1, ss.pricing);
    }
    
    if (s.extras_urn) {
      const u = s.extras_urn;
      const detail = new OrderDataItemDataUrn(s.extras_urn_detail ? s.extras_urn_detail : '', s.extras_urn?.engraving_price || 0);
      if (s.extras_urn.tag === 'photo_frame_urn') {
        detail.engraving_type = "";
        detail.engraving_price = 0;
      }
      odata.addItem('urn', u.label, u.tag, 1, u.pricing, detail);
    }

    if (s.extras_nameplate) {
      const u = s.extras_nameplate;
      const detail = new OrderDataItemDataNamePlate([s.nameplate_line1, s.nameplate_line2, s.nameplate_line3]);
      odata.addItem('nameplate', u.label, u.tag, 1, u.pricing, detail);
    }

    if (s.extras_pawprint) {
      const u = s.extras_pawprint;
      const qty = s.extras_pawprint_qty && s.extras_pawprint_qty > 0 ? s.extras_pawprint_qty : 1;
      odata.addItem('pawprint', u.label, u.tag, qty, u.pricing);
    }


    if (s.extras_addon_viewing_and_priority) {

      const u = s.extras_addon_viewing_and_priority;
      odata.addItem('viewing_and_priority_service', u.label, u.tag, 1, u.pricing);

    } else {

      if (s.extras_addon_priority) {
        const u = s.extras_addon_priority;
        odata.addItem('priority_service', u.label, u.tag, 1, u.pricing);
      }
      
      if (s.extras_addon_viewing) {
        const u = s.extras_addon_viewing;
        odata.addItem('viewing_service', u.label, u.tag, 1, u.pricing);
      }

    }

    if (s.extras_addon_after_hours_appt) {
      const u = s.extras_addon_after_hours_appt;
      odata.addItem('after_hours_appointment', u.label, u.tag, 1, u.pricing);
    }

    // add up the total
    odata.total = odata.items.reduce((t, i) => {
      let price = i.priceData.total;
      let price_additional = i.priceData.type === 'fixed_additional' ? i.priceData.total_additional || 0 : 0;
      let total = 0;
      if (i.priceData.type === 'fixed_additional') {
        total = price;
        if (i.qty > 1) {
          total += (i.qty - 1) * price_additional;
        }
      } else {
        total = (price * i.qty) + (i.data && i.data instanceof OrderDataItemDataUrn && i.data.engraving_price > 0 ? i.data.engraving_price : 0)
      }
      return t + total;
    }, 0);

    const acquirePrice = new OrderDataItemPriceData(odata.info.weight, odata.acquire.fees.find(f => f.type === odata.acquire.type)?.pricing, s.service?.tag);
    const releasePrice = new OrderDataItemPriceData(odata.info.weight, odata.release.fees.find(f => f.type === odata.release.type)?.pricing, s.service?.tag);
    odata.total += (acquirePrice.total || 0) + (releasePrice.total || 0);

    odata.comments = s.comments;

    return odata;

   
    /*
    const odata: Partial<OrderData> = {};

    const acquire = s.acquire;
    const release = s.release;

    acquire: AcquireData;
    release: ReleaseData;
    comments: string;
  
    const items = [];

    const service = s.service ? this.pricingData(s.service) : undefined;
    const urn = s.extras_urn ? this.pricingData(s.extras_urn) : undefined;
    const nameplate = s.extras_nameplate ? this.pricingData(s.extras_nameplate) : undefined;
    const pawprint = s.extras_pawprint ? this.pricingData(s.extras_pawprint) : undefined;


    const payment: Partial<OrderDataPayment> = {
    }
    */



    /*
    const extras:Partial<OrderDataExtras> = {};

    let total = 0;

    if (s.extras_urn) {
      extras.keepsake = this.pricingData(s.extras_urn);
      extras.keepsake_type = s.extras_urn_detail || "";
      total += extras.keepsake.priceData.total;
    }

    if (s.extras_nameplate) {
      extras.nameplate = this.pricingData(s.extras_nameplate);
      total += extras.nameplate.priceData.total;

      let lines = [];
      lines.push(s.nameplate_line1);
      lines.push(s.nameplate_line2);
      lines.push(s.nameplate_line3);
      extras.nameplate_lines = lines.filter(l => l.trim().length > 0);
    }

    if (s.extras_pawprint) {
      extras.pawprint = this.pricingData(s.extras_pawprint);
      extras.pawprint_qty = s.extras_pawprint_qty > 0 ? s.extras_pawprint_qty : 1;
      total += (extras.pawprint.priceData.total * extras.pawprint_qty);
    }

    const order: Partial<OrderData> = {
      info: {
        pet_name: s.pet_name,
        pet_species: s.pet_species,
        owner_name: s.owner_name,
        owner_phone: s.owner_phone,
        owner_email: s.owner_email,
        weight: s.weight || 0,
      },
      comments: s.comments.trim() || '',
    }

    if (this.props.user) {
      order.user_id = this.props.user.id;
    }

    if (s.service) {
      order.service = this.pricingData(s.service);
      total += order.service.priceData.total;
    }

    order.acquire = s.acquire || { detail: {} };
    order.release = s.release || { detail: {} };

    order.extras = (extras as OrderDataExtras);
    order.total = total;

    return (order as OrderData)
    */
  }

  /*
  pricingData = (sconf: ConfType): OrderDataItem => {
    const pdata = this.createPriceData(sconf.pricing) || ({} as OrderPriceData);
    return {
      label: sconf.label,
      tag: sconf.tag,
      priceData: pdata,
    }

    type: string;
    label: string;
    tag: string;
    qty: number;
    unit: number;
    priceData: OrderPriceData;
    data?: OrderItemData | OrderItemNameplateData;
  
  }


  createPriceData = (pconf: ConfPricing): OrderDataPriceData | undefined => {
    
    const weight = this.state.weight;
    if (!weight || weight < 0) return;

    const effective_weight = this.props.effectiveWeight(weight);

    if (pconf.pricetype === 'fixed') {
      return {
        type: pconf.pricetype,
        total: pconf.price || 0
      }

    } else if (pconf.pricetype === 'byweight' && pconf.price) {
      return {
        type: pconf.pricetype,
        total: pconf.price * effective_weight,
        price: pconf.price,
        units: effective_weight,
        descriptor: `${this.toMoney(pconf.price)}/lb x ${effective_weight}lbs`,
      }

    } else if (pconf.pricetype === 'tiered' && pconf.prices) {

      let rangeBottom = 1;
      let rangeTop = 0;
      let myprice:ConfTierPrice | undefined;

      // match the price tier with the weight
      for (let i = 0; i < pconf.prices!.length; i++) {
        let pt = pconf.prices![i];
        rangeTop = pt.max ? pt.max : 0;
        if (!pt.max || effective_weight <= pt.max) {
          myprice = pt;
          let n = pconf.prices![i - 1];
          rangeBottom = (n && n.max) ? n.max + 1 : 0;

          break;
        }
      }

      if (!myprice) return;

      let rangeStr = '';
      if (rangeTop && rangeBottom) {
        rangeStr = `${rangeBottom}-${rangeTop}lbs`;
      } else if (rangeTop && !rangeBottom) {
        rangeStr = `up to ${rangeTop}lbs`;
      } else if (rangeBottom && !rangeTop) {
        rangeStr = `${rangeBottom}+lbs`;
      }

      return {
        type: pconf.pricetype,
        total: myprice.price,
        descriptor: `${rangeStr}`,
      }
    }
  }
  */


  toMoney = (cents:number, nosign?: boolean) => {
    return toMoney(cents, nosign);
  }

}


export default OrderFormRoot;
